/**
 * Centralized image processing utilities for handling user uploads.
 * Ensures consistent validation, HEIC conversion, compression, and error handling across the app.
 */

export interface ProcessedImage {
  file: File;
  preview: string;
}

export interface ImageProcessingOptions {
  maxSizeMB?: number;
  quality?: number;
  compress?: boolean;
  maxDimension?: number;
}

const DEFAULT_OPTIONS: Required<ImageProcessingOptions> = {
  maxSizeMB: 10,
  quality: 0.8,
  compress: true,
  maxDimension: 1280,
};

/**
 * Detect if a file is in HEIC/HEIF format (iPhone's native format)
 */
export const isHeicFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".heic") ||
    name.endsWith(".heif") ||
    file.type === "image/heic" ||
    file.type === "image/heif"
  );
};

/**
 * Check if a file is a valid image type (including HEIC)
 */
export const isValidImageFile = (file: File): boolean => {
  return file.type.startsWith("image/") || isHeicFile(file);
};

/**
 * Convert HEIC file to JPEG for browser compatibility
 */
export const convertHeicToJpeg = async (
  file: File,
  quality: number = 0.8
): Promise<File> => {
  try {
    // Load heic-to (bundles libheif, ~hundreds of KB) on demand — only when a
    // user actually uploads an iPhone HEIC photo — so it stays out of the main
    // bundle.
    const { heicTo } = await import("heic-to");
    const blob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality,
    });

    const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
    return new File([blob], newName, { type: "image/jpeg" });
  } catch (error) {
    console.error("HEIC conversion failed:", error);
    throw new Error(
      "Failed to convert iPhone photo format. Please try a different image."
    );
  }
};

/**
 * Compress an image using canvas.
 * Resizes to maxDimension (preserving aspect ratio) and re-encodes as JPEG.
 * Skips compression if the file is already under 500KB.
 */
export const compressImage = async (
  file: File,
  maxDimension: number = 1920,
  quality: number = 0.8
): Promise<File> => {
  // Skip if already small
  if (file.size <= 500 * 1024) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Scale down if larger than maxDimension
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to compress image"));
            return;
          }

          // Only use compressed version if it's actually smaller
          if (blob.size >= file.size) {
            resolve(file);
            return;
          }

          const newName = file.name.replace(/\.[^.]+$/, ".jpg");
          resolve(new File([blob], newName, { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image for compression"));
    };

    img.src = url;
  });
};

/**
 * Compress an image from a URL (used for re-compressing existing photos).
 * Downloads the image, compresses via canvas, returns a Blob.
 */
export const compressImageFromUrl = async (
  imageUrl: string,
  maxDimension: number = 1920,
  quality: number = 0.8
): Promise<{ blob: Blob; originalSize: number; compressedSize: number } | null> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      let { width, height } = img;

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to compress image"));
            return;
          }
          resolve({ blob, originalSize: 0, compressedSize: blob.size });
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      // Some images may fail due to CORS — skip them
      resolve(null);
    };

    img.src = imageUrl;
  });
};

/**
 * Create a preview URL from a file using Object URLs (instant, zero-copy).
 * IMPORTANT: Callers must revoke the URL via URL.revokeObjectURL() when done.
 */
export const createPreview = (file: File): Promise<string> => {
  return Promise.resolve(URL.createObjectURL(file));
};

/**
 * Process multiple image files concurrently (up to `concurrency` at a time).
 * Calls `onReady` as each image finishes so the UI can show progressive previews.
 */
export const processImageFilesBatch = async (
  files: File[],
  options: ImageProcessingOptions = {},
  onReady?: (result: ProcessedImage, index: number) => void,
  concurrency: number = 2
): Promise<ProcessedImage[]> => {
  const results: ProcessedImage[] = new Array(files.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < files.length) {
      const i = nextIndex++;
      try {
        const result = await processImageFile(files[i], options);
        results[i] = result;
        onReady?.(result, i);
      } catch (error) {
        // Re-throw with the original file info so the caller can show which file failed
        const err = error instanceof Error ? error : new Error(String(error));
        (err as any).fileName = files[i].name;
        throw err;
      }
    }
  };

  const workers = Array.from({ length: Math.min(concurrency, files.length) }, () => worker());
  await Promise.allSettled(workers);
  return results.filter(Boolean);
};

/**
 * Process an image file: validate, convert if needed, compress, and create preview.
 * This is the main entry point for all image uploads.
 */
export const processImageFile = async (
  file: File,
  options: ImageProcessingOptions = {}
): Promise<ProcessedImage> => {
  const { maxSizeMB, quality, compress, maxDimension } = { ...DEFAULT_OPTIONS, ...options };

  if (!isValidImageFile(file)) {
    throw new Error("Please select an image file (JPG, PNG, HEIC, etc.)");
  }

  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    throw new Error(`Please select an image under ${maxSizeMB}MB`);
  }

  // Convert HEIC to JPEG if needed
  let processedFile = file;
  if (isHeicFile(file)) {
    processedFile = await convertHeicToJpeg(file, quality);
  }

  // Compress the image
  if (compress) {
    processedFile = await compressImage(processedFile, maxDimension, quality);
  }

  const preview = await createPreview(processedFile);

  return { file: processedFile, preview };
};

/**
 * Get a fallback image URL for broken images
 */
export const getFallbackImageUrl = (): string => {
  return "/placeholder.svg";
};

/**
 * Handle image load errors by setting a fallback
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement>
): void => {
  const img = event.currentTarget;
  if (img.src !== getFallbackImageUrl()) {
    img.src = getFallbackImageUrl();
  }
};
